import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { channelColor } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, X, Pencil, Trash2, Tv2, Circle } from "lucide-react";

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/channels");
    setChannels(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this channel and all its cards?")) return;
    await api.delete(`/channels/${id}`);
    toast.success("Channel deleted");
    load();
  };

  return (
    <div className="fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs text-primary tracking-[0.25em] uppercase mb-2">Portfolio</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Channels</h1>
        </div>
        <button onClick={() => setEditing({ new: true })} data-testid="new-channel-button"
          className="flex items-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-4 py-2.5 text-sm hover:bg-[#C29035] transition-colors duration-200">
          <Plus size={18} /> New channel
        </button>
      </header>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : channels.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-border rounded-lg">
          <Tv2 size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No channels yet. Create your first one.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((ch) => (
            <div key={ch.id} data-testid={`channel-card-${ch.id}`}
              className="bg-[#121214] border border-border rounded-lg p-5 hover:border-primary/40 transition-colors duration-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 rounded-md flex items-center justify-center font-display font-extrabold text-lg shrink-0" style={{ background: `${channelColor(ch.id, channels)}22`, color: channelColor(ch.id, channels) }}>
                    {ch.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-bold text-lg tracking-tight truncate">{ch.name}</p>
                    <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider ${ch.status === "active" ? "text-[#2E8B57]" : "text-muted-foreground"}`}>
                      <Circle size={7} fill="currentColor" /> {ch.status}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing(ch)} data-testid={`edit-channel-${ch.id}`} className="p-1.5 text-muted-foreground hover:text-primary rounded transition-colors duration-200"><Pencil size={15} /></button>
                  <button onClick={() => del(ch.id)} data-testid={`delete-channel-${ch.id}`} className="p-1.5 text-muted-foreground hover:text-destructive rounded transition-colors duration-200"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-4 min-h-[40px]">{ch.niche}</p>
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
                <Stat value={ch.in_progress} label="In pipeline" />
                <Stat value={ch.published_this_month} label="Published / mo" />
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ChannelModal key={editing.new ? "new-channel" : editing.id} channel={editing.new ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-mono text-2xl font-bold">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function ChannelModal({ channel, onClose, onSaved }) {
  const isNew = !channel;
  const [form, setForm] = useState({
    name: channel?.name || "",
    niche: channel?.niche || "",
    status: channel?.status || "active",
  });
  useEffect(() => {
    setForm({
      name: channel?.name || "",
      niche: channel?.niche || "",
      status: channel?.status || "active",
    });
  }, [channel]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/channels", form);
      else await api.put(`/channels/${channel.id}`, form);
      toast.success(isNew ? "Channel created" : "Channel updated");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid="channel-modal"
          className="relative w-full max-w-md max-h-[90vh] flex flex-col bg-[#121214] border border-border rounded-lg fade-up"
        >
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="font-display font-bold text-xl tracking-tight">{isNew ? "New channel" : "Edit channel"}</h2>
            <button onClick={onClose} data-testid="close-channel-modal" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
          </div>
          <div className="flex-1 min-h-0 px-6 py-5 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Name</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="channel-name-input"
                className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200" placeholder="NightVault" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Niche</label>
              <textarea value={form.niche} onChange={(e) => set("niche", e.target.value)} rows={2} data-testid="channel-niche-input"
                className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200 resize-none" placeholder="Horror & mystery narration" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="channel-status-select"
                className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200">
                <option value="active">Active</option><option value="paused">Paused</option>
              </select>
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
            <button onClick={onClose} data-testid="cancel-channel-button" className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-channel-button"
              className="flex items-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-5 py-2.5 text-sm hover:bg-[#C29035] transition-colors duration-200 disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={16} /> : null}{isNew ? "Create" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
